import {ChevronDownIcon, ChevronUpIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AfriexBank, AfriexTransactionProcessor} from '@/types';

type ProcessorCode = {bankCode: string; swiftCode?: string};

// Th/Td carry no horizontal padding of their own; each table sets it.
const FIRST_CELL = 'pl-6 pr-4';
const CELL = 'px-4';
const EXPANDED_TINT = 'bg-gray-50 dark:bg-gray-900';

const IDENTIFIER_LABELS: Record<string, string> = {
  swift_code: 'SWIFT',
  sort_code: 'Sort code',
  bank_code: 'Bank code',
  cnaps_code: 'CNAPS',
  routing_number: 'Routing number',
};

const processorEntries = (
  bank: AfriexBank,
  selected?: AfriexTransactionProcessor,
): [string, ProcessorCode][] => {
  const entries = Object.entries(bank.processorIdentifiers ?? {}).filter(
    (entry): entry is [string, ProcessorCode] => Boolean(entry[1]?.bankCode),
  );
  // The selected processor first, then the rest alphabetically.
  const selectedName: string | undefined = selected;
  return entries.sort(([a], [b]) => {
    if (a === selectedName) {
      return -1;
    }
    if (b === selectedName) {
      return 1;
    }
    return a.localeCompare(b);
  });
};

const formatProcessorCode = (code?: ProcessorCode) =>
  code ? [code.bankCode, code.swiftCode].filter(Boolean).join(' / ') : '-';

type BankTableProps = {
  items: AfriexBank[];
  // Set when the list is filtered to one processor: adds a column with the
  // code this bank is actually sent to that processor as.
  processor?: AfriexTransactionProcessor;
};

const BankTable = ({items, processor}: BankTableProps) => (
  <Table className="overflow-hidden">
    <Thead>
      <Tr>
        <Th className={FIRST_CELL}>Bank</Th>
        <Th className={CELL}>Country</Th>
        <Th className={CELL}>Afriex code</Th>
        {processor ? <Th className={CELL}>{processor} code</Th> : null}
        <Th className={CELL}>Processors</Th>
      </Tr>
    </Thead>
    <TBody>
      {items.map((bank, index) => (
        <BankRow
          key={bank.id ?? `${bank.countryCode}-${bank.code}`}
          bank={bank}
          index={index}
          processor={processor}
        />
      ))}
    </TBody>
  </Table>
);

const BankRow = ({
  bank,
  index,
  processor,
}: {
  bank: AfriexBank;
  index: number;
  processor?: AfriexTransactionProcessor;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  // An open row and its detail panel share one tint, so they read as a unit.
  const rowTint = isExpanded ? EXPANDED_TINT : zebraRow;
  const processors = processorEntries(bank, processor);
  const identifiers = Object.entries(bank.identifiers ?? {}).filter(
    ([, value]) => Boolean(value),
  );
  const columnCount = processor ? 5 : 4;
  const selectedName: string | undefined = processor;

  return (
    <>
      <Tr className={rowTint}>
        <Td className={FIRST_CELL}>
          <div className="font-medium">{bank.name}</div>
          {bank.branch ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {bank.branch}
            </div>
          ) : null}
        </Td>
        <Td className={CELL}>{bank.countryCode}</Td>
        <Td className={CELL}>
          <span className="font-mono">{bank.code}</span>
        </Td>
        {processor ? (
          <Td className={CELL}>
            <span className="font-mono text-indigo-700 dark:text-indigo-300">
              {formatProcessorCode(bank.processorIdentifiers?.[processor])}
            </span>
          </Td>
        ) : null}
        <Td className={CELL}>
          <Button
            variant="unStyled"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} codes for ${bank.name}`}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <span className="inline-flex items-center gap-1">
              {processors.length}
              {isExpanded ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )}
            </span>
          </Button>
        </Td>
      </Tr>
      {isExpanded ? (
        <Tr className={`${EXPANDED_TINT} border-t-0`} enableHover={false}>
          <Td
            colspan={columnCount}
            className={`${FIRST_CELL} pt-0 whitespace-normal`}
          >
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 py-2">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Identifiers
                </div>
                {identifiers.length ? (
                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                    {identifiers.map(([type, value]) => (
                      <div key={type} className="contents">
                        <dt className="text-gray-500 dark:text-gray-400">
                          {IDENTIFIER_LABELS[type] ?? type}
                        </dt>
                        <dd className="font-mono">{value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="text-sm text-gray-500">No identifiers</p>
                )}
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Processor codes
                </div>
                {processors.length ? (
                  <ul className="flex flex-wrap gap-2">
                    {processors.map(([name, code]) => (
                      <li
                        key={name}
                        className={`font-mono text-xs rounded border px-2 py-1 ${
                          name === selectedName
                            ? 'border-indigo-400 text-indigo-700 dark:text-indigo-300'
                            : 'border-gray-300 dark:border-dark-borderLight'
                        }`}
                      >
                        {name} {formatProcessorCode(code)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">
                    Not mapped to any processor
                  </p>
                )}
              </div>
            </div>
          </Td>
        </Tr>
      ) : null}
    </>
  );
};

export default BankTable;
